import type { CSSProperties, ReactElement, ReactNode } from "react";
import { color } from "@tokens/index";
import { useViewport } from "@primitives/index";
import { LiveUpdatesBanner } from "./LiveUpdatesBanner";
import { contentBlockPadding, contentGutter } from "./shellMeasures";

/**
 * ShellFrame: the full-viewport column every page renders into.
 *
 * Top bar, live-updates banner, optional sub bar, then `<main>`. The banner
 * is closed unless realtime stays down. The canvas gradient lives here
 * and nowhere else. `content="contain"` gives the main region a fixed
 * height chain so a child can own its own scroll; `content="scroll"` lets
 * the main region scroll. `inlineGutter={false}` hands the horizontal
 * gutter to the child; a full-bleed scene uses it and owns its own inset.
 */

export type ShellContentMode = "contain" | "scroll";

export interface ShellFrameProps {
  readonly topBar: ReactNode;
  readonly subBar?: ReactNode;
  /** Default `"contain"`. */
  readonly content?: ShellContentMode;
  /** Default `true`. */
  readonly inlineGutter?: boolean;
  readonly children: ReactNode;
}

const rootStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  height: "100dvh",
  background: color.gradient.canvas,
  color: color.fg.default,
  boxSizing: "border-box",
  position: "relative",
  isolation: "isolate",
  overflow: "hidden",
};

export function ShellFrame({
  topBar,
  subBar,
  content = "contain",
  inlineGutter = true,
  children,
}: ShellFrameProps): ReactElement {
  const { viewport } = useViewport();
  const gutter = contentGutter(viewport);

  const mainStyle: CSSProperties = {
    position: "relative",
    zIndex: 1,
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingInline: inlineGutter ? gutter : 0,
    ...(content === "scroll"
      ? {
          overflowY: "auto",
          overscrollBehavior: "contain",
          paddingBlock: contentBlockPadding(viewport),
        }
      : { overflow: "hidden" }),
  };

  return (
    <div style={rootStyle} data-shell-frame="">
      {topBar}
      <LiveUpdatesBanner gutter={gutter} />
      {subBar}
      <main style={mainStyle}>{children}</main>
    </div>
  );
}
